import { DestroyRef } from '@angular/core';
import { fromEvent, Subject, zip } from 'rxjs';
import { HTMLElementUtils, StaticDependenciesService } from '@cinetixx/cinetixx-ui';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ResizeEvents } from '../../enums';
import { CreatorStateService } from './CreatorStateService';
import { filter } from 'rxjs/operators';

export class ResizeService {

  public readonly elementResized$ = new Subject<{
    el: HTMLElement,
    prevDimensions: { width: number, height: number, webX: number, webY: number }
  }>();
  public readonly afterMouseDown$ = new Subject<{
    el: HTMLElement,
    event: MouseEvent
  }>();
  public readonly beforeMouseMove$ = new Subject<{
    el: HTMLElement,
    event: MouseEvent
  }>();
  public readonly afterMouseMove$ = new Subject<{
    el: HTMLElement,
    resizeAction: ResizeEvents,
    current: { width: number, height: number, webX: number, webY: number },
    previous: { width: number, height: number, webX: number, webY: number }
  }>();

  private _blockResize = false;
  private _resizeAction: ResizeEvents;
  private _resizeElement: HTMLElement;
  private _originalWidth = 0;
  private _originalHeight = 0;
  private _originalX = 0;
  private _originalY = 0;
  private _elDimensions: { width: number, height: number, webX: number, webY: number } = null;
  private _container: HTMLElement;
  private _resizeElementClassName: string;

  public constructor(
    private readonly _destroyRef: DestroyRef
  ) {
    CreatorStateService.currentStep$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((step) => this._blockResize = step === 'zones' || step === 'connections');
  }

  public init(resizeElementClassName: string, container?: HTMLElement): void {
    this._container = container ?? CreatorStateService.roomRef.nativeElement;
    this._resizeElementClassName = resizeElementClassName;

    fromEvent<MouseEvent>(StaticDependenciesService.document, 'mouseup').pipe(
      takeUntilDestroyed(this._destroyRef),
      filter(() => !this._blockResize)
    ).subscribe(this.onMouseUp.bind(this));

    fromEvent<MouseEvent>(this._container, 'mousedown').pipe(
      takeUntilDestroyed(this._destroyRef),
      filter(() => !this._blockResize)
    ).subscribe(this.onMouseDown.bind(this));

    fromEvent<MouseEvent>(this._container, 'mousemove').pipe(
      takeUntilDestroyed(this._destroyRef),
      filter(() => !!this._resizeAction && !this._blockResize)
    ).subscribe(this.onMouseMove.bind(this));
  }

  public onMouseUp(): void {
    if (this._elDimensions && this._resizeElement) {
      this.elementResized$.next({
        el: this._resizeElement,
        prevDimensions: {
          width: this._elDimensions.width,
          height: this._elDimensions.height,
          webX: this._elDimensions.webX,
          webY: this._elDimensions.webY,
        }
      });
    }

    this._resizeAction = null;
    this._resizeElement = null;
    this._originalWidth = 0;
    this._originalHeight = 0;
    this._originalX = 0;
    this._originalY = 0;
    this._elDimensions = null;
  }

  public onMouseDown(event: MouseEvent): void {
    this._resizeElement = (event.target as HTMLElement).closest(`.${this._resizeElementClassName}`) as HTMLElement;

    if (this._resizeElement && (event.target as HTMLElement).dataset['resizeEvent']) {
      this._resizeAction = (event.target as HTMLElement).dataset['resizeEvent'] as ResizeEvents;
      this._elDimensions = {
        width: this._resizeElement.offsetWidth,
        height: this._resizeElement.offsetHeight,
        webX: this._resizeElement.offsetLeft,
        webY: this._resizeElement.offsetTop
      };
      this._originalWidth = this._resizeElement.offsetWidth;
      this._originalHeight = this._resizeElement.offsetHeight;
      this._originalX = this._resizeElement.offsetLeft;
      this._originalY = this._resizeElement.offsetTop;

      this.afterMouseDown$.next({
        el: this._resizeElement,
        event
      });
    }
  }

  public onMouseMove(event: MouseEvent): void {
    this.beforeMouseMove$.next({el: this._resizeElement, event});

    let width = 0;
    let height = 0;
    const rect = this._container.getBoundingClientRect();
    const scale = HTMLElementUtils.getScale(this._container);
    const left = (event.clientX - rect.left) / scale;
    const top = (event.clientY - rect.top) / scale;
    const right = event.clientX - rect.right;
    const bottom = event.clientY - rect.bottom;

    if (left < 0 || top < 0 || right > 0 || bottom > 0) {
      return;
    }

    switch (this._resizeAction) {
      case ResizeEvents.TOP_LEFT:
        width = this._originalWidth - (left - this._originalX);
        height = this._originalHeight - (top - this._originalY);

        if (width < 0 || height < 0) {
          return;
        }

        this._resizeElement.style.top = `${top}px`;
        this._resizeElement.style.left = `${left}px`;
        break;
      case ResizeEvents.TOP_RIGHT:
        width = left - this._originalX;
        height = this._originalHeight - (top - this._originalY);

        if (width < 0 || height < 0) {
          return;
        }

        this._resizeElement.style.top = `${top}px`;
        break;
      case ResizeEvents.BOTTOM_LEFT:
        width = this._originalWidth - (left - this._originalX);
        height = top - this._originalY;

        if (width < 0 || height < 0) {
          return;
        }

        this._resizeElement.style.left = `${left}px`;
        break;
      case ResizeEvents.BOTTOM_RIGHT:
        width = left - this._originalX;
        height = top - this._originalY;

        if (width < 0 || height < 0) {
          return;
        }
        break;
    }

    this._resizeElement.style.width = `${Math.abs(width).toFixed()}px`;
    this._resizeElement.style.height = `${Math.abs(height).toFixed()}px`;

    this.afterMouseMove$.next({
      el: this._resizeElement,
      resizeAction: this._resizeAction,
      previous: {
        width: this._elDimensions.width,
        height: this._elDimensions.height,
        webX: this._elDimensions.webX,
        webY: this._elDimensions.webY,
      },
      current: {
        width: +Math.abs(width).toFixed(),
        height: +Math.abs(height).toFixed(),
        webX: left,
        webY: top,
      }
    })
  }

  public createResizePoints(resizeElement: HTMLElement): void {
    const createPoint = (top: string, left: string, transform: string, cursor: string, event: ResizeEvents) => {
      const point = StaticDependenciesService.document.createElement('div');
      point.classList.add('resize-point');
      point.style.position = 'absolute';
      point.style.top = top;
      point.style.left = left;
      point.style.transform = transform;
      point.style.cursor = cursor;
      point.dataset['resizeEvent'] = event;
      return point;
    };

    const points = [
      { top: '0', left: '0', transform: 'translate(-50%, -50%)', cursor: 'nw-resize', event: ResizeEvents.TOP_LEFT },
      { top: '0', left: '100%', transform: 'translate(-50%, -50%)', cursor: 'ne-resize', event: ResizeEvents.TOP_RIGHT },
      { top: '100%', left: '0', transform: 'translate(-50%, -50%)', cursor: 'sw-resize', event: ResizeEvents.BOTTOM_LEFT },
      { top: '100%', left: '100%', transform: 'translate(-50%, -50%)', cursor: 'se-resize', event: ResizeEvents.BOTTOM_RIGHT }
    ];

    for (const point of points) {
      resizeElement.appendChild(
        createPoint(point.top, point.left, point.transform, point.cursor, point.event)
      );
    }
  }

  public removeResizePoints(resizeElement: HTMLElement): void {
    resizeElement.querySelectorAll('.resize-point').forEach((point) => point.remove());
  }
}
